
import Component from "vue-class-component";
import Vue from "vue";
import {Prop} from "vue-property-decorator";
import {
    CertificateTypeApiModel,
    CountryLightModel,
    DocumentAttributesClient,
    EnterForeignCertificateRequest,
    EnterForeignCertificatesClient,
    ResultError,
    TempFileModel
} from "@/api-client";
import Multiselect from "vue-multiselect";
import FileUploader from "@/components/FileUploader.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import DatePicker from "@/components/DatePicker.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import FormLayout from "@/features/personProfile/components/FormLayout.vue";
import {NavigationGuardNext} from "vue-router/types/router";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import DropdownSearch from "@/components/DropdownSearch.vue";
import moment from "moment";
import ConfirmModal from "@/features/shared/components/ConfirmModal.vue";
import { ls } from "@/CultureService";

interface Props {
    seafarerId: string;
    certificateTypes: CertificateTypeApiModel[];
    countries: CountryLightModel[];
}

@Component({
    components: {
        Multiselect,
        FileUploader,
        HeadingGroup,
        DatePicker,
        LabelOptional,
        FormLayout,
        DropdownSearch,
        ConfirmModal
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<EnterForeignCertificate>) {
        const props = to.meta!.props as Props;
        props.seafarerId = to.params.seafarerId;
        Promise.all([
            new DocumentAttributesClient().foreignSeagoingCertificateTypes(),
            new DocumentAttributesClient().countries()
        ]).then(values => {
            props.certificateTypes = values[0];
            props.countries = values[1];
            next();
        }).catch(e => next(e))
    }
})
export default class EnterForeignCertificate extends Vue implements Props {
    @Prop({required: true})
    seafarerId!: string;
    @Prop({required: true})
    certificateTypes!: CertificateTypeApiModel[];
    @Prop({required: true})
    countries!: CountryLightModel[];

    validationErrors = ResultError.createEmpty();
    selectedCertificateType: CertificateTypeApiModel | null = null;
    certificateName = "";
    documentNumber = "";
    issuerCountryCode: string | null = null;
    issueDate: Date | null = null;
    validUntil: Date | null = null;
    additionalInfo = "";
    addedFiles: TempFileModel[] = [];
    fileLoader = new Loader();

    $refs!: {
        confirmModal: ConfirmModal
    };

    confirmWarning: string | null = null;

    async created() {
        this.$watch(() => this.issueDate, () => {
            if(this.issueDate && moment().startOf("day").diff(this.issueDate) < 0){
                this.validationErrors.clearKey("issueDate");
                this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issueDate");
            }
            else{
                this.validationErrors.clearKey("issueDate");
            }
        });

        this.$watch(() => this.validUntil, () => {
            if(this.issueDate && this.validUntil){
                if(moment(this.validUntil).diff(this.issueDate) <= 0){
                    this.validationErrors.clearKey("validUntil");
                    this.validationErrors.appendMessage(this.$ls.Global_ValidUntil_Before_IssuedOn,"validUntil");
                }
                else{
                    this.validationErrors.clearKey("validUntil");
                }
            }
        });
    }

    async submit() {
        this.validationErrors.clear();

        await new EnterForeignCertificatesClient()
            .submit(new EnterForeignCertificateRequest({
                documentNumber: this.documentNumber,
                certificateTypeId: this.selectedCertificateType != null ? this.selectedCertificateType.id : null,
                issueDate: this.issueDate,
                validUntil: this.validUntil,
                additionalInfo: this.additionalInfo,
                addedFiles: this.addedFiles,
                issuerCountryCode: this.issuerCountryCode
            }), this.seafarerId)
            .then(
                async (certificateId) => {
                    await this.$router.push({name: "certificate-details", params: {id: certificateId}});
                    toasts.success(this.$ls.Global_Toast_SaveSuccess);
                },
                error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure));
    }

    async validateBeforeSubmit()
    {
        this.validationErrors.clear();

        await new EnterForeignCertificatesClient()
            .validate(new EnterForeignCertificateRequest({
                documentNumber: this.documentNumber,
                certificateTypeId: this.selectedCertificateType != null ? this.selectedCertificateType.id : null,
                issueDate: this.issueDate,
                validUntil: this.validUntil,
                additionalInfo: this.additionalInfo,
                addedFiles: this.addedFiles,
                issuerCountryCode: this.issuerCountryCode
            }), this.seafarerId)
            .then(
                async () => {
                    this.submit();
                },
                error =>  {
                    if ("duplicate" in error?.messages)
                    {
                        this.confirmWarning =
                            Array.isArray(error.messages.duplicate) ? error.messages.duplicate.join(" ") : error.messages.duplicate;
                        this.$refs.confirmModal.show();
                    } else {
                        this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure);
                    }
                });
    }
}
